// AIReviewProcessForm.tsx — Create / edit / clone an AI review process (modal)
import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { saveProcess, uploadLetterhead, AIReviewProcess, PdfConfig, ScheduleConfig } from '../../services/aiReview';
import { toast } from 'react-toastify';

interface Props {
  existing?: AIReviewProcess | null;
  /** When set (and `existing` is not), pre-fills the form from this process as a new, paused copy. */
  cloneOf?: AIReviewProcess | null;
  /** Every process, used to keep names — and therefore email subjects — unique. */
  allProcesses?: { id: number; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}

const ROLE_OPTIONS = [
  { value: 'partner',       label: 'Job Partner' },
  { value: 'manager',       label: 'Job Manager' },
  { value: 'current_staff', label: 'Current Staff (who has the job)' },
  { value: 'client_partner', label: 'Client Partner' },
  { value: 'client_manager', label: 'Client Manager' },
];

const DEFAULT_SYSTEM_PROMPT =
  'You are a senior CPA performing a quality control review of a tax return prepared by BMSS, LLC. ' +
  'Identify potential errors, inconsistencies, missing items, and planning opportunities. ' +
  'Be specific: reference form names and line items when flagging issues. ' +
  'Use professional CPA language suitable for an internal quality review memo. ' +
  'Do not speculate about information not present in the data.';

const DEFAULT_USER_TEMPLATE =
  'Please review the following tax return data and produce a QC memo.\n' +
  'Flag any issues, inconsistencies, or items requiring attention.\n\n' +
  'Format your response as:\n' +
  '**SUMMARY**: 2-3 sentence overview\n' +
  '**KEY FIGURES**: Bullet list of the most significant financial figures\n' +
  '**ISSUES / FLAGS**: Numbered list of items requiring attention or verification\n' +
  '**OBSERVATIONS**: Any notable planning opportunities or informational items\n\n' +
  '--- TAX RETURN DATA ---\n' +
  '{TAX_DATA}';

// Names are compared ignoring case and repeated whitespace.
const normName = (n: string) => n.trim().replace(/s+/g, ' ').toLowerCase();

export default function AIReviewProcessForm({ existing, cloneOf, allProcesses = [], onClose, onSaved }: Props) {
  const isNew   = !existing;
  const isClone = isNew && !!cloneOf;
  const source  = existing ?? cloneOf ?? null;

  // A clone starts with a blank name so it can't be saved as an unchanged copy.
  const [name,          setName]          = useState(isClone ? '' : (source?.name ?? ''));
  const [processType,   setProcessType]   = useState(source?.process_type  ?? 'full_return');
  const [isActive,      setIsActive]      = useState(isClone ? false : (source?.is_active ?? true)); // clones start paused
  const [workStatus,    setWorkStatus]    = useState(source?.pe_work_status ?? '');
  const [exportScope,   setExportScope]   = useState(source?.export_scope  ?? 'federal');
  const [model,         setModel]         = useState(source?.model          ?? 'gpt-4.1');
  const provider = model.startsWith('claude-') ? 'claude' : 'openai';
  const [dedupDays,     setDedupDays]     = useState(source?.dedup_days     ?? 1);
  const [systemPrompt,  setSystemPrompt]  = useState(source ? (source.system_prompt ?? '') : DEFAULT_SYSTEM_PROMPT);
  const [userTemplate,  setUserTemplate]  = useState(source ? (source.user_template ?? '') : DEFAULT_USER_TEMPLATE);
  const [emailRoles,    setEmailRoles]    = useState<string[]>(() => {
    if (!source) return ['partner', 'manager']; // default for brand-new processes only
    try { return JSON.parse(source.email_to_roles ?? '[]'); } catch { return []; }
  });
  const [emailAddrs,    setEmailAddrs]    = useState(
    (() => { try { return (JSON.parse(source?.email_to_addrs ?? '[]') as string[]).join(', '); } catch { return ''; } })()
  );
  const [returnTypes,   setReturnTypes]   = useState(
    (() => { try { return (JSON.parse(source?.return_types ?? '[]') as string[]).join(', '); } catch { return ''; } })()
  );
  // pdf_config state
  const [pdfPrimaryColor,  setPdfPrimaryColor]  = useState<string>(() => {
    try { return JSON.parse(source?.pdf_config ?? '{}').primaryColor  || '#1a3a6b'; } catch { return '#1a3a6b'; }
  });
  const [pdfHeaderBg,      setPdfHeaderBg]      = useState<string>(() => {
    try { return JSON.parse(source?.pdf_config ?? '{}').headerBg      || '#1e293b'; } catch { return '#1e293b'; }
  });
  const [pdfFirmName,      setPdfFirmName]      = useState<string>(() => {
    try { return JSON.parse(source?.pdf_config ?? '{}').firmName      || ''; } catch { return ''; }
  });
  const [pdfLetterheadUrl, setPdfLetterheadUrl] = useState<string>(() => {
    try { return JSON.parse(source?.pdf_config ?? '{}').letterheadUrl || ''; } catch { return ''; }
  });
  const [uploadingLh, setUploadingLh] = useState(false);

  // schedule_config state
  const [schedInterval,  setSchedInterval]  = useState<number>(() => {
    try { return JSON.parse(source?.schedule_config ?? '{}').intervalMinutes  ?? 15; } catch { return 15; }
  });
  const [schedStartHour, setSchedStartHour] = useState<number>(() => {
    try { return JSON.parse(source?.schedule_config ?? '{}').windowStartHour  ?? 8; }  catch { return 8; }
  });
  const [schedEndHour,   setSchedEndHour]   = useState<number>(() => {
    try { return JSON.parse(source?.schedule_config ?? '{}').windowEndHour    ?? 17; } catch { return 17; }
  });
  const [schedTimezone,  setSchedTimezone]  = useState<string>(() => {
    try { return JSON.parse(source?.schedule_config ?? '{}').timezone ?? 'America/Chicago'; } catch { return 'America/Chicago'; }
  });
  const [schedDays, setSchedDays] = useState<number[]>(() => {
    try {
      const d = JSON.parse(source?.schedule_config ?? '{}').daysOfWeek;
      return Array.isArray(d) ? d : [1, 2, 3, 4, 5];
    } catch { return [1, 2, 3, 4, 5]; }
  });
  const [blackouts, setBlackouts] = useState<{ from: string; to: string }[]>(() => {
    try { return JSON.parse(source?.schedule_config ?? '{}').blackoutRanges ?? []; } catch { return []; }
  });
  const [blackoutFrom, setBlackoutFrom] = useState('');
  const [blackoutTo,   setBlackoutTo]   = useState('');

  const [tab,    setTab]    = useState<'basic' | 'scope' | 'ai' | 'email' | 'style' | 'schedule'>('basic');
  const [saving, setSaving] = useState(false);

  async function handleLetterheadUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLh(true);
    try {
      const { url } = await uploadLetterhead(file);
      setPdfLetterheadUrl(url);
      toast.success('Letterhead uploaded.');
    } catch (err: any) {
      toast.error('Upload failed: ' + err.message);
    } finally {
      setUploadingLh(false);
      e.target.value = '';
    }
  }

  function toggleRole(role: string) {
    setEmailRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  }

  async function handleSave() {
    if (!name.trim())       { toast.error('Name is required.'); return; }
    // The name is the first part of the email subject, so a duplicate name means a duplicate subject.
    const clash = allProcesses.find(p => p.id !== existing?.id && normName(p.name) === normName(name));
    if (clash) {
      toast.error(isClone && clash.id === cloneOf?.id
        ? `Give the clone a new name — "${clash.name}" is the process you're copying.`
        : `A process named "${clash.name}" already exists. Each name must be unique because it becomes the email subject.`);
      return;
    }
    if (!workStatus.trim()) { toast.error('PE Work Status is required.'); return; }

    const emailAddrArr = emailAddrs.split(',').map(s => s.trim()).filter(Boolean);
    const returnTypeArr = returnTypes.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);

    // Build pdf_config — only include non-default / set values
    const pdfCfg: PdfConfig = {};
    if (pdfPrimaryColor  && pdfPrimaryColor  !== '#1a3a6b') pdfCfg.primaryColor  = pdfPrimaryColor;
    if (pdfHeaderBg      && pdfHeaderBg      !== '#1e293b') pdfCfg.headerBg      = pdfHeaderBg;
    if (pdfFirmName.trim())                                  pdfCfg.firmName      = pdfFirmName.trim();
    if (pdfLetterheadUrl.trim())                             pdfCfg.letterheadUrl = pdfLetterheadUrl.trim();
    const pdfConfigJson = Object.keys(pdfCfg).length ? JSON.stringify(pdfCfg) : null;

    // Build schedule_config
    const schedCfg: ScheduleConfig = {
      intervalMinutes: schedInterval,
      windowStartHour: schedStartHour,
      windowEndHour:   schedEndHour,
      timezone:        schedTimezone,
      daysOfWeek:      schedDays,
      blackoutRanges:  blackouts,
    };
    const schedConfigJson = JSON.stringify(schedCfg);

    setSaving(true);
    try {
      await saveProcess({
        id:             existing?.id,
        name:           name.trim(),
        process_type:   processType as any,
        is_active:      isActive,
        pe_work_status: workStatus.trim(),
        export_scope:   exportScope as any,
        model,
        dedup_days:     dedupDays,
        system_prompt:  systemPrompt || null,
        user_template:  userTemplate || null,
        pdf_config:       pdfConfigJson,
        schedule_config:  schedConfigJson,
        email_to_roles: JSON.stringify(emailRoles),
        email_to_addrs: emailAddrArr.length ? JSON.stringify(emailAddrArr) : null,
        return_types:   returnTypeArr.length ? JSON.stringify(returnTypeArr) : null,
        tax_years:      null,
      });
      toast.success(isClone ? 'Process cloned (paused — activate it when ready).' : isNew ? 'Process created.' : 'Process updated.');
      onSaved();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={overlay}>
      <div style={modal}>
        {/* Header */}
        <div style={modalHeader}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>
            {isClone ? `Clone: ${cloneOf!.name}` : isNew ? 'New AI Review Process' : `Edit: ${existing!.name}`}
          </span>
          <button onClick={onClose} style={iconBtn}><X size={16} /></button>
        </div>

        {/* Tabs */}
        <div style={tabBar}>
          {(['basic', 'scope', 'ai', 'email', 'style', 'schedule'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ ...tabBtn, ...(tab === t ? tabBtnActive : {}) }}>
              {t === 'basic' ? 'Basic' : t === 'scope' ? 'Scope' : t === 'ai' ? 'AI Prompt'
                : t === 'email' ? 'Email' : t === 'style' ? 'Styling' : 'Schedule'}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={tabContent}>

          {tab === 'basic' && (
            <div style={fields}>
              {isClone && (
                <div style={{ fontSize: 12, lineHeight: 1.5, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'rgba(122,162,255,0.08)' }}>
                  Cloning <strong>{cloneOf!.name}</strong>. Every setting is copied — give it a new name below.
                  The copy is saved <strong>paused</strong> so it doesn't start emailing until you activate it.
                </div>
              )}
              <Field label="Process Name">
                <input style={input} value={name} onChange={e => setName(e.target.value)}
                  placeholder={isClone ? `New name (not "${cloneOf!.name}")` : 'e.g. S-Corp Detail Review'} />
                <small style={hint}>
                  Email subject: <em>{name.trim() || '<Process Name>'}: Client Name — Job Name</em>. The name must be unique so each process's emails are distinguishable.
                </small>
              </Field>
              <Field label="Process Type">
                <select style={input} value={processType} onChange={e => setProcessType(e.target.value as 'full_return' | 'delivery_doc')}>
                  <option value="full_return">Full Return Review</option>
                  <option value="delivery_doc">Delivery Document Review</option>
                </select>
              </Field>
              <Field label="PE Work Status to Monitor">
                <input style={input} value={workStatus} onChange={e => setWorkStatus(e.target.value)}
                  placeholder="e.g. Review, Detail Review, Partner Review" />
                <small style={hint}>Exact match to JobWorkStatus in Practice Engine</small>
              </Field>
              <Field label="AI Provider">
                <select style={input} value={provider} onChange={e => {
                  setModel(e.target.value === 'claude' ? 'claude-opus-4-6' : 'gpt-4.1');
                }}>
                  <option value="openai">OpenAI</option>
                  <option value="claude">Anthropic (Claude)</option>
                </select>
              </Field>
              <Field label="Model">
                {provider === 'claude' ? (
                  <select style={input} value={model} onChange={e => setModel(e.target.value)}>
                    <option value="claude-opus-4-6">Claude Opus 4.6 — best quality, recommended for tax analysis</option>
                    <option value="claude-sonnet-4-6">Claude Sonnet 4.6 — fast, strong quality</option>
                    <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5 — fastest, most economical</option>
                  </select>
                ) : (
                  <select style={input} value={model} onChange={e => setModel(e.target.value)}>
                    <option value="gpt-4.1">GPT-4.1 (recommended)</option>
                    <option value="gpt-4.1-mini">GPT-4.1 Mini (faster / cheaper)</option>
                    <option value="gpt-4o">GPT-4o</option>
                    <option value="gpt-4o-mini">GPT-4o Mini</option>
                    <option value="o3">o3 (highest quality, slowest)</option>
                    <option value="o4-mini">o4-mini (reasoning, cost-effective)</option>
                  </select>
                )}
              </Field>
              <Field label="Dedup Window (days)">
                <input style={{ ...input, width: 80 }} type="number" min={1} max={30}
                  value={dedupDays} onChange={e => setDedupDays(Number(e.target.value))} />
                <small style={hint}>Minimum days between reviews of the same return</small>
              </Field>
              <label style={checkRow}>
                <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                <span style={{ marginLeft: 8 }}>Active (scheduler will monitor this process)</span>
              </label>
            </div>
          )}

          {tab === 'scope' && (
            <div style={fields}>
              <Field label="Return Scope">
                <select style={input} value={exportScope} onChange={e => setExportScope(e.target.value as any)}>
                  <option value="federal">Federal only</option>
                  <option value="state">State only</option>
                  <option value="both">Federal + State</option>
                </select>
              </Field>
              <Field label="Return Types (optional filter)">
                <input style={input} value={returnTypes} onChange={e => setReturnTypes(e.target.value)}
                  placeholder="e.g. S, C, F, I  (comma-separated, blank = all)" />
                <small style={hint}>
                  S = 1120S, C = 1120, F = 1041, I = 1040, P = 1065. Leave blank to include all types.
                </small>
              </Field>
              <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 8 }}>
                Tax year filtering is available via the API (tax_years field) but not yet exposed here — the scheduler will process all years by default.
              </div>
            </div>
          )}

          {tab === 'ai' && (
            <div style={fields}>
              <Field label="System Prompt">
                <textarea
                  style={{ ...input, height: 140, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
                  value={systemPrompt}
                  onChange={e => setSystemPrompt(e.target.value)}
                  placeholder="Leave blank to use the server default prompt"
                />
                <small style={hint}>
                  {isNew ? 'Sets the AI\'s persona and review approach.' : 'Showing the saved prompt. Leave blank to use the server default.'}
                </small>
              </Field>
              <Field label="User Prompt Template">
                <textarea
                  style={{ ...input, height: 200, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
                  value={userTemplate}
                  onChange={e => setUserTemplate(e.target.value)}
                  placeholder="Leave blank to use the server default template"
                />
                <small style={hint}>
                  Use <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>{'{TAX_DATA}'}</code> where
                  the return data should be inserted. Add{' '}
                  <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>{'{PY_TAX_DATA}'}</code> as
                  well and the review also receives the client's prior-year return from CCH (when there is one).
                  Leave it out and nothing about the review changes.{' '}
                  {!isNew && 'Leave blank to use the server default.'}
                </small>
              </Field>
            </div>
          )}

          {tab === 'style' && (
            <div style={fields}>
              <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 16, lineHeight: 1.5 }}>
                All styling is optional — leave fields at their defaults for the standard BMSS dark-navy look.
                Changes apply to PDF attachments only; they do not affect the AI&rsquo;s output.
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <Field label="Section header color">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="color" value={pdfPrimaryColor} onChange={e => setPdfPrimaryColor(e.target.value)}
                      style={{ width: 40, height: 32, padding: 2, borderRadius: 4, border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--surface)' }} />
                    <input style={{ ...input, flex: 1 }} value={pdfPrimaryColor}
                      onChange={e => setPdfPrimaryColor(e.target.value)}
                      placeholder="#1a3a6b" />
                  </div>
                  <small style={hint}>Used for ## section headings and metadata labels</small>
                </Field>
                <Field label="Header bar background">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="color" value={pdfHeaderBg} onChange={e => setPdfHeaderBg(e.target.value)}
                      style={{ width: 40, height: 32, padding: 2, borderRadius: 4, border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--surface)' }} />
                    <input style={{ ...input, flex: 1 }} value={pdfHeaderBg}
                      onChange={e => setPdfHeaderBg(e.target.value)}
                      placeholder="#1e293b" />
                  </div>
                  <small style={hint}>Background of the top header bar on each PDF</small>
                </Field>
              </div>

              <Field label="Firm / sub-title name">
                <input style={input} value={pdfFirmName} onChange={e => setPdfFirmName(e.target.value)}
                  placeholder="BMSS Automated Quality Review" />
                <small style={hint}>Appears in the header sub-line. Leave blank for the default.</small>
              </Field>

              <Field label="Letterhead image">
                {pdfLetterheadUrl ? (
                  <div style={{ marginBottom: 8 }}>
                    <img src={pdfLetterheadUrl} alt="Letterhead preview"
                      style={{ width: '100%', maxHeight: 120, objectFit: 'contain', objectPosition: 'left',
                               border: '1px solid var(--border)', borderRadius: 4, background: '#fff', padding: 8 }} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      <button type="button" style={secondaryBtn}
                        onClick={() => document.getElementById('lh-upload')?.click()}>
                        Replace
                      </button>
                      <button type="button" style={{ ...secondaryBtn, color: '#ef4444', borderColor: '#ef4444' }}
                        onClick={() => setPdfLetterheadUrl('')}>
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" style={secondaryBtn} disabled={uploadingLh}
                    onClick={() => document.getElementById('lh-upload')?.click()}>
                    {uploadingLh ? 'Uploading…' : 'Upload image…'}
                  </button>
                )}
                <input id="lh-upload" type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={handleLetterheadUpload} />
                <small style={hint}>
                  When set, the letterhead image replaces the dark header bar on the PDF.
                  PNG or JPEG recommended, full-width (e.g. 1200&times;200 px). Max 5 MB.
                </small>
              </Field>

              {/* Live preview swatch */}
              <div style={{ marginTop: 8, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
                {pdfLetterheadUrl
                  ? <img src={pdfLetterheadUrl} alt="" style={{ width: '100%', maxHeight: 80, objectFit: 'cover', display: 'block' }} />
                  : <div style={{ background: pdfHeaderBg, padding: '10px 16px', transition: 'background 0.2s',
                                  WebkitPrintColorAdjust: 'exact' }}>
                      <div style={{ fontWeight: 700, color: '#fff', fontSize: 13 }}>AI Tax Return Review</div>
                      <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>
                        {pdfFirmName || 'BMSS Automated Quality Review'} &mdash; {name || 'Process Name'}
                      </div>
                    </div>
                }
                <div style={{ background: 'var(--surface)', padding: '8px 16px', borderTop: '1px solid var(--border)' }}>
                  <span style={{ fontWeight: 700, color: pdfPrimaryColor, fontSize: 13, transition: 'color 0.2s' }}>
                    ## 1. Client &amp; Return Overview
                  </span>
                </div>
              </div>
              <small style={{ ...hint, marginTop: 6 }}>Live preview of header and section heading color</small>
            </div>
          )}

          {tab === 'email' && (
            <div style={fields}>
              <Field label="Send email to:">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                  {ROLE_OPTIONS.map(r => (
                    <label key={r.value} style={checkRow}>
                      <input type="checkbox" checked={emailRoles.includes(r.value)}
                        onChange={() => toggleRole(r.value)} />
                      <span style={{ marginLeft: 8 }}>{r.label}</span>
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Additional recipients (comma-separated emails)">
                <input style={input} value={emailAddrs} onChange={e => setEmailAddrs(e.target.value)}
                  placeholder="e.g. supervisor@bmss.com, director@bmss.com" />
              </Field>
              <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 4 }}>
                Emails are sent from <strong>keithbotfield@bmss.com</strong> as an HTML memo with the review content.
              </div>
            </div>
          )}

          {tab === 'schedule' && (
            <div style={fields}>
              <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 16, lineHeight: 1.5 }}>
                The scheduler fires every 15 minutes. Each process checks its own settings below before running.
                All times are evaluated in the selected timezone.
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <Field label="Run interval">
                  <select style={input} value={schedInterval} onChange={e => setSchedInterval(Number(e.target.value))}>
                    <option value={15}>Every 15 minutes</option>
                    <option value={30}>Every 30 minutes</option>
                    <option value={60}>Every 1 hour</option>
                    <option value={120}>Every 2 hours</option>
                    <option value={240}>Every 4 hours</option>
                    <option value={480}>Every 8 hours</option>
                    <option value={1440}>Once daily</option>
                  </select>
                  <small style={hint}>Minimum time between runs for this process</small>
                </Field>
                <Field label="Timezone">
                  <select style={input} value={schedTimezone} onChange={e => setSchedTimezone(e.target.value)}>
                    <option value="America/Chicago">Central (CST/CDT)</option>
                    <option value="America/New_York">Eastern (EST/EDT)</option>
                    <option value="America/Denver">Mountain (MST/MDT)</option>
                    <option value="America/Los_Angeles">Pacific (PST/PDT)</option>
                    <option value="America/Phoenix">Arizona (MST, no DST)</option>
                    <option value="UTC">UTC</option>
                  </select>
                </Field>
              </div>

              <Field label="Active hours">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <select style={{ ...input, width: 140 }} value={schedStartHour} onChange={e => setSchedStartHour(Number(e.target.value))}>
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={h}>{fmtHour(h)}</option>
                    ))}
                  </select>
                  <span style={{ color: 'var(--muted)', fontSize: 13 }}>to</span>
                  <select style={{ ...input, width: 140 }} value={schedEndHour} onChange={e => setSchedEndHour(Number(e.target.value))}>
                    {Array.from({ length: 25 }, (_, h) => (
                      <option key={h} value={h}>{h === 24 ? '11:59 PM (end of day)' : fmtHour(h)}</option>
                    ))}
                  </select>
                </div>
                <small style={hint}>Runs only start within this window (end hour is exclusive)</small>
              </Field>

              <Field label="Active days">
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d, i) => (
                    <label key={d} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                      fontSize: 12, padding: '4px 10px', borderRadius: 6,
                      background: schedDays.includes(i) ? 'var(--accent)' : 'var(--surface)',
                      color: schedDays.includes(i) ? '#fff' : 'var(--muted)',
                      border: '1px solid var(--border)', userSelect: 'none' }}>
                      <input type="checkbox" style={{ display: 'none' }}
                        checked={schedDays.includes(i)}
                        onChange={() => setSchedDays(prev =>
                          prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i].sort()
                        )} />
                      {d}
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="Blackout periods (annual)">
                {blackouts.map((b, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 13, flex: 1 }}>
                      {b.from} → {b.to}
                      <small style={{ color: 'var(--muted)', marginLeft: 6 }}>
                        ({monthLabel(b.from)} – {monthLabel(b.to)})
                      </small>
                    </span>
                    <button type="button" style={{ ...secondaryBtnSm, color: '#f7768e', borderColor: '#f7768e44' }}
                      onClick={() => setBlackouts(prev => prev.filter((_, j) => j !== i))}>
                      Remove
                    </button>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                  <input style={{ ...input, width: 100 }} value={blackoutFrom}
                    onChange={e => setBlackoutFrom(e.target.value)}
                    placeholder="MM-DD" maxLength={5} />
                  <span style={{ color: 'var(--muted)', fontSize: 13 }}>to</span>
                  <input style={{ ...input, width: 100 }} value={blackoutTo}
                    onChange={e => setBlackoutTo(e.target.value)}
                    placeholder="MM-DD" maxLength={5} />
                  <button type="button" style={secondaryBtnSm}
                    onClick={() => {
                      if (!blackoutFrom.match(/^\d{2}-\d{2}$/) || !blackoutTo.match(/^\d{2}-\d{2}$/)) {
                        toast.error('Use MM-DD format for blackout dates.');
                        return;
                      }
                      setBlackouts(prev => [...prev, { from: blackoutFrom, to: blackoutTo }]);
                      setBlackoutFrom('');
                      setBlackoutTo('');
                    }}>
                    Add
                  </button>
                </div>
                <small style={hint}>
                  Dates in MM-DD format. Blackouts are annual and inclusive. E.g. 05-01 → 07-31 skips May through July every year.
                </small>
              </Field>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={modalFooter}>
          <button onClick={onClose} style={secondaryBtn}>Cancel</button>
          <button onClick={handleSave} style={primaryBtn} disabled={saving}>
            <Save size={14} style={{ marginRight: 5 }} />
            {saving ? 'Saving…' : isNew ? 'Create Process' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtHour(h: number): string {
  if (h === 0)  return '12:00 AM (midnight)';
  if (h < 12)   return `${h}:00 AM`;
  if (h === 12) return '12:00 PM (noon)';
  return `${h - 12}:00 PM`;
}

function monthLabel(mmdd: string): string {
  const months = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const m = parseInt(mmdd.split('-')[0], 10);
  return months[m] ?? mmdd;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const modal: React.CSSProperties = {
  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
  width: 640, maxHeight: '88vh', display: 'flex', flexDirection: 'column',
  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
};
const modalHeader: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '14px 20px', borderBottom: '1px solid var(--border)',
};
const modalFooter: React.CSSProperties = {
  display: 'flex', justifyContent: 'flex-end', gap: 10,
  padding: '12px 20px', borderTop: '1px solid var(--border)',
};
const tabBar: React.CSSProperties = {
  display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 16px',
};
const tabBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', padding: '10px 14px',
  fontSize: 13, color: 'var(--muted)', borderBottom: '2px solid transparent',
};
const tabBtnActive: React.CSSProperties = {
  color: 'var(--accent)', borderBottomColor: 'var(--accent)', fontWeight: 600,
};
const tabContent: React.CSSProperties = { padding: '20px 24px', overflowY: 'auto', flex: 1 };
const fields: React.CSSProperties = {};
const input: React.CSSProperties = {
  width: '100%', background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 4, color: 'var(--text)', padding: '7px 10px', fontSize: 13,
  boxSizing: 'border-box',
};
const hint: React.CSSProperties = { color: 'var(--muted)', fontSize: 11, marginTop: 4, display: 'block' };
const checkRow: React.CSSProperties = { display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: 13 };
const iconBtn: React.CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', display: 'flex' };
const primaryBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', background: 'var(--accent)', color: '#fff',
  border: 'none', borderRadius: 5, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600,
};
const secondaryBtnSm: React.CSSProperties = {
  background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)',
  borderRadius: 4, padding: '5px 10px', fontSize: 12, cursor: 'pointer',
};
const secondaryBtn: React.CSSProperties = {
  background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)',
  borderRadius: 5, padding: '8px 14px', fontSize: 13, cursor: 'pointer',
};
